import { useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { pageVariants, pageTransition } from '../../constants/GlobalStyles';
import AppContext from '../../contexts/app-context';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Charts
import AnalysisPageLineChart from '../../components/Charts/line/AnalysisPageLineChart';
// import StateChart from '../../components/Charts/pie/StateChart';
import State from '../../components/Charts/piev2/State';
import Statistics from '../../components/Charts/statistics/Statistics';
// import MostUsedDrugsPieChart from '../../components/Charts/pie/MostUsedDrugsPieChart';
import TopDrugs from '../../components/Charts/piev2/TopDrugs';

// import MostUsedIcdsPieChart from '../../components/Charts/pie/MostUsedIcdsPieChart';
import TopDiagnosis from '../../components/Charts/piev2/TopDiagnosis';

// import MostUsedCptsPieChart from '../../components/Charts/pie/MostUsedCptsPieChart';
import TopProcedures from '../../components/Charts/piev2/TopProcedures';

import useInsuranceAnalysisData from './useInsuranceAnalysisData';
import FilterModal from '../../components/filter/FilterModal';

// Icons for the statistic cards
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import GroupIcon from '@mui/icons-material/Group';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SavingsIcon from '@mui/icons-material/Savings';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { statsTitles, departmentLabel, divisionLabel } from '../../components/config/variables';
import TuneIcon from '@mui/icons-material/Tune';


const FacilityAnalysisPage = () => {
  const appCtx = useContext(AppContext);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    department: '',
    division: '',
    doctor: '',
  });
  // used to pass to the filters modal to update the filters after delete a spesific filter
  const [newFiltersAfterDelete, setNewFiltersAfterDelete] = useState(undefined)

  const type = 'date_filtered'

  // Apply filters -> build global query string for children
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    const { fromDate, toDate, department, division, doctor } = newFilters;
    const params = [];
    if (fromDate) params.push(`from_date=${fromDate}`);
    if (toDate) params.push(`to_date=${toDate}`);
    if (department) params.push(`department=${department}`);
    if (division) params.push(`division=${division}`);
    if (doctor) params.push(`doctor=${doctor}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    appCtx.handleSetGlobalFilterQuery(queryString);
    setIsFilterModalOpen(false);
  };

  // Fetch stats from API
  const { statsData } = useInsuranceAnalysisData(type);

  // Suffixes
  const symbol = {
    total_expenditure: '$',
    approved_ratio: '%',
    avg_cost: '$',
    money_saved: '$',
  };

  const params = new URLSearchParams(appCtx.globalFilterQuery);

  const department  = params.get('department');
  const division  = params.get('division');
  const doctor = params.get('doctor');
  const from_date = params.get('from_date');
  const to_date = params.get('to_date');

  const deleteSpesificFilter = (filterKey) => {
    filters[filterKey] = "";
    handleApplyFilters(filters)
    setNewFiltersAfterDelete(filters)

};

  // Icons (passed per key to <Statistics />)
  const statisticIcons = {
    total_expenditure: (
      <AttachMoneyIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    approved_ratio: (
      <CheckCircleIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    total_encounters: (
      <EventAvailableIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    avg_cost: (
      <RequestQuoteIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    total_patients: (
      <GroupIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    active_doctors: (
      <MedicalServicesIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    top_drug: (
      <CalendarTodayIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    money_saved: (
      <SavingsIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
    >
      <Box sx={{ mt: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {/* Configure Filters */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.5,
              py: 0.7,
              borderRadius: '10px',
              bgcolor: 'primary.main',
              color: 'white',
              fontSize: 16,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { opacity: 0.85 },
            }}
            onClick={() => setIsFilterModalOpen(true)}
          >
            <TuneIcon fontSize="small" />
            <Typography sx={{fontWeight: '500'}}>All Filters</Typography>
          </Box>

          {/* Active Filters */}
          {[
            { label: 'From Date', value: from_date, key: 'fromDate' },
            { label: 'To Date', value: to_date, key: 'toDate' },
            { label: 'Department', value: department && departmentLabel[department], key: 'department' },
            { label: 'Division', value: division && divisionLabel[division], key: 'division' },
            { label: 'Doctor', value: doctor, key: 'doctor' },
          ]
            .filter(f => f.value)
            .map(f => (
              <Box
                key={f.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.3,
                  py: 0.5,
                  borderRadius: '10px',
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  fontSize: 16,
                  transition: 'all 0.2s ease',
                  '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,0.1)' },
                }}
              >
                <Typography sx={{ fontWeight: 500 }}>
                  {f.label}: {f.value}
                </Typography>
                <HighlightOffIcon
                  onClick={() => deleteSpesificFilter(f.key)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: 18,
                    color: 'text.secondary',
                    '&:hover': { color: 'error.main' },
                  }}
                />
              </Box>
            ))}
        </Box>
      </Box>

      <FilterModal
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleApplyFilters}
          newFiltersAfterDelete={newFiltersAfterDelete}
          initialFilters={filters}
          fields={["date", "department", "division", "doctor"]}
        />
 
      <Box sx={{ position: 'relative' }}>
          <Statistics
            data={statsData}
            cards={['total_expenditure', 'approved_ratio', 'total_encounters', 'avg_cost']}
            titles={statsTitles}
            icons={statisticIcons}
            symbol={symbol}
            customStyle={{ border: '1px solid #d9d9d9', padding: '10px', borderRadius: '10px 10px 0px 0px', display: 'flex', justifyContent: 'space-around' }}
          />
          <Statistics
            data={statsData}
            cards={['total_patients', 'active_doctors', 'top_drug', 'money_saved']}
            titles={statsTitles}
            icons={statisticIcons}
            symbol={symbol}
            customStyle={{ border: '1px solid #d9d9d9', padding: '10px', marginBottom: '10px', borderRadius: '0px 0px 10px 10px', display: 'flex', justifyContent: 'space-around', borderTop: 'none' }}

          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            border: '1px solid #d9d9d9',
            borderRadius: '10px',
            alignItems: 'center',
            padding: '15px',
            minHeight: '360px'
          }}
        >
          <Box sx={{ width: '70%' }}>
            <AnalysisPageLineChart />
          </Box>
          <Box sx={{ width: '30%' }}>
            <State />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 2,
            border: '1px solid #d9d9d9',
            borderRadius: '10px',
            mt: 2,
            p: 2,
            marginBottom: '10px',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ p: 2, borderRight: { md: '1px solid #eee' } }}>
            <TopDrugs allTime={true} chartId="drugs-chart-1" />
          </Box>

          <Box sx={{ p: 2, borderRight: { md: '1px solid #eee' } }}>
            <TopDiagnosis />
          </Box>

          <Box sx={{ p: 2 }}>
            <TopProcedures chartId="CPTs-chart" />
          </Box>
        </Box>
    </motion.div>
  );
};

export default FacilityAnalysisPage;